import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  purchaseWorkspaceSchema,
  type PurchaseWorkspaceValues,
  localBusinessDate,
} from '@klothiq/validation';
import { purchaseTotals, saleMoney } from '@klothiq/domain';
import {
  usePurchase,
  usePurchasePayments,
  usePurchaseWorkspace,
  useCreateOrUpdatePurchase,
} from '@/hooks/usePurchases';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useProducts } from '@/hooks/useProducts';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import PageHeader from '@/components/common/PageHeader';
import DatePicker from '@/components/common/DatePicker';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import PurchasePaymentFields, {
  purchaseInputClass as input,
  emptyPurchasePayment,
  checkedPurchasePayment,
} from '@/components/purchases/PurchasePaymentFields';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const newLine = (direct = false): PurchaseWorkspaceValues['items'][number] => ({
  item_kind: direct ? 'manual' : 'catalog',
  variant_id: null,
  description: '',
  inventory_treatment: direct ? ('' as 'non_stock') : 'tracked',
  quantity: 1,
  unit_cost: 0,
  discount: 0,
});
export default function PurchaseFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const confirm = useConfirm();
  const existing = usePurchase(id);
  const payments = usePurchasePayments(id);
  const workspace = usePurchaseWorkspace();
  const suppliers = useSuppliers();
  const products = useProducts();
  const save = useCreateOrUpdatePurchase();
  const intent = usePurchaseIntent(id || 'new');
  const initialized = useRef('');
  const version = useRef<string>();
  const [pageError, setPageError] = useState('');
  const [correctionId, setCorrectionId] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correction, setCorrection] = useState(emptyPurchasePayment);
  const [payNow, setPayNow] = useState(false);
  const [payment, setPayment] = useState(emptyPurchasePayment);
  const {
    register,
    control,
    watch,
    reset,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<PurchaseWorkspaceValues>({
    resolver: zodResolver(purchaseWorkspaceSchema),
    defaultValues: {
      supplier_id: '',
      purchase_date: localBusinessDate(),
      expected_date: '',
      supplier_invoice_no: '',
      entry_mode: 'catalog',
      status: 'draft',
      other_charges: 0,
      notes: '',
      items: [newLine()],
    },
  });
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'items' });
  const values = watch();
  const frozen =
    !!existing.data && ['partially_received', 'received'].includes(existing.data.status);
  const working = isSubmitting || save.isPending;
  useEffect(() => {
    const p = existing.data;
    if (!p || initialized.current === p.id) return;
    initialized.current = p.id;
    version.current = p.updated_at;
    reset({
      supplier_id: p.supplier_id,
      purchase_date: p.purchase_date,
      expected_date: p.expected_date || '',
      supplier_invoice_no: p.supplier_invoice_no || '',
      entry_mode: p.entry_mode,
      status: p.status as PurchaseWorkspaceValues['status'],
      other_charges: Number(p.other_charges),
      notes: p.notes || '',
      items: p.purchase_items.map((i) => ({
        item_id: i.id,
        variant_id: i.variant_id,
        item_kind: i.item_kind,
        description: i.description || '',
        inventory_treatment: i.inventory_treatment,
        quantity: i.quantity,
        unit_cost: Number(i.unit_cost),
        discount: Number(i.discount),
      })),
    });
  }, [existing.data, reset]);
  const totals = purchaseTotals(values.items || [], Number(values.other_charges) || 0);
  const variants = (products.data || []).flatMap((p) =>
    (p.product_variants || []).map((v) => ({
      id: v.id,
      label: `${p.name} · ${v.sku} (${v.size}${v.color ? ' / ' + v.color : ''})`,
    })),
  );
  async function mode(next: 'catalog' | 'direct') {
    if (next === values.entry_mode) return;
    if (
      !(await confirm({
        title: 'Change purchase entry?',
        description: 'This replaces the current item lines. Supplier and order details will stay.',
        confirmText: 'Replace items',
        cancelText: 'Keep current items',
      }))
    )
      return;
    setValue('entry_mode', next, { shouldDirty: true });
    replace([newLine(next === 'direct')]);
  }
  const submit = handleSubmit(async (data) => {
    setPageError('');
    try {
      const paid = Number(existing.data?.paid_amount || 0);
      const checked = payNow ? checkedPurchasePayment(payment, totals.total - paid) : undefined;
      if (payNow && data.status !== 'ordered')
        throw new Error('Set the purchase to Ordered before recording payment.');
      const selectedPayment = payments.data?.find((p) => p.id === correctionId && !p.is_void);
      if (correctionId && !selectedPayment)
        throw new Error('Reload payment history before correcting this payment.');
      if (
        selectedPayment &&
        (correctionReason.trim().length < 3 || correctionReason.trim().length > 500)
      )
        throw new Error('Enter a payment correction reason (3–500 characters).');
      const edits = selectedPayment
        ? [
            {
              ...checkedPurchasePayment(
                correction,
                totals.total - paid + Number(selectedPayment.amount),
              ),
              payment_id: selectedPayment.id,
              expected_updated_at: selectedPayment.updated_at,
              reason: correctionReason.trim(),
            },
          ]
        : undefined;
      let payload = {
        ...data,
        payment_edits: edits,
        expected_date: data.expected_date || null,
        purchase_id: id,
        expected_updated_at: version.current,
        payment: checked,
      };
      const send = () => save.mutateAsync({ ...payload, request_key: intent.key('save', payload) });
      let result;
      try {
        result = await send();
      } catch (error) {
        if (
          !(error instanceof Error) ||
          !error.message.startsWith('DUPLICATE_INVOICE:') ||
          !(await confirm({
            title: 'Supplier invoice already exists',
            description:
              error.message.replace('DUPLICATE_INVOICE:', '') +
              ' Continue only if this is a separate purchase.',
            confirmText: 'Confirm separate purchase',
            variant: 'warning',
          }))
        )
          throw error;
        payload = { ...payload, duplicate_invoice_confirmed: true } as typeof payload;
        result = await send();
      }
      intent.complete('save');
      toast.success(id ? 'Purchase updated' : 'Purchase created');
      navigate(`/purchases/${result.purchase_id}`);
    } catch (error) {
      setPageError(
        error instanceof Error
          ? error.message
          : 'Purchase could not be saved. Your entries have been kept.',
      );
    }
  });
  if (id && existing.isLoading) return <LoadingState message="Loading purchase…" />;
  if (id && existing.error)
    return <ErrorState message={existing.error.message} onRetry={existing.refetch} />;
  if (
    existing.data &&
    (existing.data.is_voided ||
      !['draft', 'ordered', 'partially_received', 'received'].includes(existing.data.status))
  )
    return <ErrorState message="This purchase is read only and cannot be edited." />;
  if (!can(id ? 'purchases.edit' : 'purchases.create'))
    return <ErrorState message="You do not have permission to edit purchases." />;
  return (
    <div className="mx-auto max-w-4xl pb-6">
      <PageHeader
        title={id ? 'Edit purchase' : 'New purchase'}
        description="Record supplier purchases, track receiving and manage payment."
        backTo={id ? `/purchases/${id}` : '/purchases'}
      />
      {workspace.error && (
        <ErrorState message={workspace.error.message} onRetry={workspace.refetch} />
      )}
      {workspace.isLoading && (
        <p role="status" className="mb-4 text-sm text-muted-foreground">
          Checking purchase service…
        </p>
      )}
      {workspace.data === false && (
        <p
          role="alert"
          className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
        >
          The purchase database update is required before saving. Existing purchases are still
          available to view.
        </p>
      )}
      <form onSubmit={submit} noValidate className="space-y-5">
        <fieldset disabled={working} className="space-y-5">
          <section className="obsidian-surface space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
            <h2 className="font-semibold">Order details</h2>
            <div aria-label="Purchase entry" className="grid grid-cols-2 gap-2">
              {(['catalog', 'direct'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  disabled={!!id || frozen || !workspace.data}
                  aria-pressed={values.entry_mode === m}
                  onClick={() => void mode(m)}
                  className={`min-h-12 rounded-xl border px-3 text-sm font-medium ${values.entry_mode === m ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}
                >
                  {m === 'catalog' ? 'Select products' : 'Direct entry'}
                </button>
              ))}
            </div>
            {values.entry_mode === 'direct' && (
              <p className="text-sm text-muted-foreground">
                Type your items below. Choose whether each item needs stock tracking.
              </p>
            )}
            {frozen && (
              <p className="rounded-xl bg-muted p-3 text-sm">
                Goods have been received. You can update the invoice reference, expected date and
                notes; received items stay protected.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1 text-sm" htmlFor="po-supplier">
                <span>Supplier *</span>
                <Controller
                  control={control}
                  name={'supplier_id'}
                  render={({ field: selectField, fieldState: selectState }) => (
                    <Select
                      id="po-supplier"
                      {...selectField}
                      disabled={frozen}
                      className={input}
                      aria-invalid={selectState.invalid || undefined}
                      value={selectField.value ?? ''}
                      helpLabel="Supplier *"
                    >
                      <option value="">Select supplier</option>
                      {suppliers.data?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                      {existing.data?.suppliers &&
                        !suppliers.data?.some((s) => s.id === existing.data!.supplier_id) && (
                          <option value={existing.data.supplier_id}>{existing.data.suppliers.name} (inactive)</option>
                        )}
                    </Select>
                  )}
                />
                <span className="text-destructive">{errors.supplier_id?.message}</span>
              </label>
              <label className="space-y-1 text-sm" htmlFor="po-invoice">
                <span>Invoice reference (optional)</span>
                <Input
                  id="po-invoice"
                  {...register('supplier_invoice_no')}
                  maxLength={100}
                  className={input}
                  helpLabel="Invoice reference (optional)"
                />
              </label>
              <div className="space-y-1 text-sm">
                <label htmlFor="po-date">Purchase date *</label>
                <Controller
                  control={control}
                  name="purchase_date"
                  render={({ field }) => (
                    <DatePicker
                      id="po-date"
                      ref={field.ref}
                      value={field.value}
                      onChange={(v) => field.onChange(v || '')}
                      onBlur={field.onBlur}
                      disabled={frozen || working}
                      clearable={false}
                      helpLabel="Purchase date *"
                    />
                  )}
                />
                <p className="text-destructive">{errors.purchase_date?.message}</p>
              </div>
              <div className="space-y-1 text-sm">
                <label htmlFor="po-expected">Expected delivery (optional)</label>
                <Controller
                  control={control}
                  name="expected_date"
                  render={({ field }) => (
                    <DatePicker
                      id="po-expected"
                      ref={field.ref}
                      value={field.value}
                      onChange={(v) => field.onChange(v || '')}
                      onBlur={field.onBlur}
                      disabled={working}
                      helpLabel="Expected delivery (optional)"
                    />
                  )}
                />
                <p className="text-destructive">{errors.expected_date?.message}</p>
              </div>
              <label className="space-y-1 text-sm" htmlFor="po-status">
                <span>Status</span>
                <Controller
                  control={control}
                  name={'status'}
                  render={({ field: selectField, fieldState: selectState }) => (
                    <Select
                      id="po-status"
                      {...selectField}
                      disabled={frozen}
                      className={input}
                      aria-invalid={selectState.invalid || undefined}
                      value={selectField.value ?? ''}
                      helpLabel="Status"
                    >
                      {(frozen || existing.data?.status === 'ordered'
                        ? [existing.data!.status]
                        : ['draft', 'ordered']
                      ).map((s) => (
                        <option key={s} value={s}>
                          {s.replace('_', ' ')}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </label>
              <label className="space-y-1 text-sm" htmlFor="po-charges">
                <span>Other charges (₹)</span>
                <Input
                  id="po-charges"
                  type="number"
                  min="0"
                  step="0.01"
                  {...register('other_charges', { valueAsNumber: true })}
                  disabled={frozen}
                  className={input}
                  helpLabel="Other charges (₹)"
                />
                <span className="text-destructive">{errors.other_charges?.message}</span>
              </label>
            </div>
            {(suppliers.error || products.error) && (
              <p role="alert" className="text-sm text-destructive">
                Some selections could not load.{' '}
                <button
                  type="button"
                  onClick={() => {
                    void suppliers.refetch();
                    void products.refetch();
                  }}
                  className="underline"
                >
                  Retry
                </button>
              </p>
            )}
          </section>
          <section className="obsidian-surface space-y-4 rounded-2xl border border-border bg-card p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold">Items</h2>
              {!frozen && (
                <button
                  type="button"
                  onClick={() => append(newLine(values.entry_mode === 'direct'))}
                  className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm"
                >
                  <Plus size={16} /> Add item
                </button>
              )}
            </div>
            {typeof errors.items?.message === 'string' && (
              <p role="alert" className="text-sm text-destructive">
                {errors.items.message}
              </p>
            )}
            {fields.map((field, index) => (
              <fieldset
                disabled={frozen}
                key={field.id}
                className="space-y-3 rounded-xl border border-border bg-muted/20 p-3 sm:p-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Item {index + 1}</h3>
                  {!frozen && fields.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove item ${index + 1}`}
                      onClick={() => remove(index)}
                      className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {values.items[index]?.item_kind === 'manual' ? (
                  <>
                    <label className="block space-y-1 text-sm" htmlFor={`po-description-${index}`}>
                      <span>Description *</span>
                      <Input
                        id={`po-description-${index}`}
                        {...register(`items.${index}.description`)}
                        maxLength={300}
                        className={input}
                        placeholder="e.g. Packaging bags"
                        helpLabel="Description *"
                      />
                      <span className="text-destructive">
                        {errors.items?.[index]?.description?.message}
                      </span>
                    </label>
                    <label className="block space-y-1 text-sm" htmlFor={`po-stock-${index}`}>
                      <span>Stock handling *</span>
                      <Controller
                        control={control}
                        name={`items.${index}.inventory_treatment`}
                        render={({ field: selectField, fieldState: selectState }) => (
                          <Select
                            id={`po-stock-${index}`}
                            {...selectField}
                            className={input}
                            aria-invalid={selectState.invalid || undefined}
                            value={selectField.value ?? ''}
                            helpLabel="Stock handling *"
                          >
                            <option value="">Choose stock handling</option>
                            <option value="non_stock">No stock tracking</option>
                            <option value="link_required">Link to stock later</option>
                          </Select>
                        )}
                      />
                      <span className="text-destructive">
                        {errors.items?.[index]?.inventory_treatment?.message}
                      </span>
                    </label>
                  </>
                ) : (
                  <label className="block space-y-1 text-sm" htmlFor={`po-product-${index}`}>
                    <span>Product / variant *</span>
                    <Controller
                      control={control}
                      name={`items.${index}.variant_id`}
                      render={({ field: selectField, fieldState: selectState }) => (
                        <Select
                          id={`po-product-${index}`}
                          {...selectField}
                          className={input}
                          aria-invalid={selectState.invalid || undefined}
                          value={selectField.value ?? ''}
                          helpLabel="Product / variant *"
                        >
                          <option value="">Select product</option>
                          {variants.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.label}
                            </option>
                          ))}
                          {values.items[index]?.variant_id &&
                            !variants.some((v) => v.id === values.items[index]?.variant_id) && (
                              <option value={values.items[index]?.variant_id || ''}>
                                {existing.data?.purchase_items.find((i) => i.id === values.items[index]?.item_id)
                                  ?.product_variants?.products?.name || 'Existing product'}{' '}
                                (inactive)
                              </option>
                            )}
                        </Select>
                      )}
                    />
                    <span className="text-destructive">
                      {errors.items?.[index]?.variant_id?.message}
                    </span>
                  </label>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {(['quantity', 'unit_cost', 'discount'] as const).map((key) => (
                    <label key={key} htmlFor={`po-${key}-${index}`} className="space-y-1 text-sm">
                      <span>
                        {key === 'quantity'
                          ? 'Quantity'
                          : key === 'unit_cost'
                            ? 'Unit cost (₹)'
                            : 'Discount (₹)'}
                      </span>
                      <Input
                        id={`po-${key}-${index}`}
                        type="number"
                        min={key === 'quantity' ? 1 : 0}
                        step={key === 'quantity' ? 1 : '0.01'}
                        {...register(`items.${index}.${key}`, { valueAsNumber: true })}
                        className={input}
                        helpLabel={key.replaceAll('_', ' ')}
                      />
                      <span className="text-destructive">
                        {errors.items?.[index]?.[key]?.message}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-right text-sm font-semibold">
                  Total:{' '}
                  {saleMoney(
                    (Number(values.items[index]?.quantity) || 0) *
                      (Number(values.items[index]?.unit_cost) || 0) -
                      (Number(values.items[index]?.discount) || 0),
                  )}
                </p>
              </fieldset>
            ))}
            <div className="ml-auto max-w-sm space-y-2 border-t border-border pt-4 text-sm">
              {[
                ['Subtotal', totals.subtotal],
                ['Total discount', totals.discount],
                ['Other charges', Number(values.other_charges) || 0],
                ['Total', totals.total],
              ].map(([label, amount]) => (
                <div
                  key={String(label)}
                  className={`flex justify-between gap-4 ${label === 'Total' ? 'border-t border-border pt-3 text-base font-bold' : ''}`}
                >
                  <span>{label}</span>
                  <span>{saleMoney(Number.isFinite(Number(amount)) ? Number(amount) : 0)}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="obsidian-surface space-y-3 rounded-2xl border border-border bg-card p-4">
            <label htmlFor="po-notes" className="block text-sm font-medium">
              Notes (optional)
            </label>
            <Textarea
              id="po-notes"
              {...register('notes')}
              maxLength={1000}
              rows={3}
              className={input}
              helpLabel="Purchase notes"
            />
            <p className="text-sm text-destructive">{errors.notes?.message}</p>
            {!id && can('purchases.pay') && (
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <Input
                  type="checkbox"
                  checked={payNow}
                  onChange={(e) => setPayNow(e.target.checked)}
                  className="h-4 w-4"
                  helpLabel="Record supplier payment now"
                />{' '}
                Record supplier payment now
              </label>
            )}
            {payNow && values.status !== 'ordered' && (
              <p className="text-sm text-amber-600">
                Set Status to Ordered to record payment with this purchase.
              </p>
            )}
            {payNow && (
              <PurchasePaymentFields value={payment} onChange={setPayment} disabled={working} />
            )}
            {!!id && !frozen && can('purchases.correct') && (
              <div className="space-y-3 border-t border-border pt-4">
                <label htmlFor="po-correct-payment" className="block space-y-1 text-sm">
                  <span>Correct a payment with this save (optional)</span>
                  <Select
                    id="po-correct-payment"
                    value={correctionId}
                    onChange={(e) => {
                      setCorrectionId(e.target.value);
                      const p = payments.data?.find((v) => v.id === e.target.value);
                      if (p)
                        setCorrection({
                          amount: String(p.amount),
                          account_id: p.account_id,
                          payment_method: p.payment_method,
                          payment_date: p.payment_date,
                          reference_number: p.reference_number || '',
                          notes: p.notes || '',
                        });
                    }}
                    className={input}
                    helpLabel="Correct a payment with this save (optional)"
                  >
                    <option value="">Keep existing payments</option>
                    {payments.data
                      ?.filter((p) => !p.is_void)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.payment_date} · {saleMoney(p.amount)} · {p.payment_method}
                        </option>
                      ))}
                  </Select>
                </label>
                {payments.error && (
                  <p role="alert" className="text-sm text-destructive">
                    Payment history could not load.{' '}
                    <button
                      type="button"
                      onClick={() => void payments.refetch()}
                      className="underline"
                    >
                      Retry
                    </button>
                  </p>
                )}
                {correctionId && (
                  <>
                    <PurchasePaymentFields
                      id="po-correction"
                      value={correction}
                      onChange={setCorrection}
                      disabled={working}
                    />
                    <label htmlFor="po-correction-reason" className="block space-y-1 text-sm">
                      <span>Correction reason *</span>
                      <Textarea
                        id="po-correction-reason"
                        value={correctionReason}
                        onChange={(e) => setCorrectionReason(e.target.value)}
                        minLength={3}
                        maxLength={500}
                        className={input}
                        helpLabel="Correction reason *"
                      />
                    </label>
                  </>
                )}
              </div>
            )}
          </section>
        </fieldset>
        {pageError && (
          <div
            role="alert"
            className="space-y-2 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
          >
            <p>{pageError}</p>
            <button
              type="button"
              disabled={working}
              onClick={async () => {
                try {
                  const prior = await intent.recover('save');
                  if (prior?.purchase_id) {
                    toast.info('The previous save completed. Opening the saved purchase.');
                    navigate(`/purchases/${prior.purchase_id}`);
                  } else
                    toast.info('No completed save was found. You can retry using the same form.');
                } catch (e) {
                  setPageError(
                    e instanceof Error ? e.message : 'Could not check the previous save',
                  );
                }
              }}
              className="min-h-11 underline"
            >
              Check previous save
            </button>
            {!!id && (
              <button
                type="button"
                disabled={working}
                onClick={async () => {
                  if (await confirm('Reload the latest purchase and discard unsaved form changes?'))
                    window.location.reload();
                }}
                className="min-h-11 underline"
              >
                Reload latest purchase
              </button>
            )}
          </div>
        )}
        <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-background/95 py-3 pb-[max(.75rem,env(safe-area-inset-bottom))] backdrop-blur">
          <button
            type="button"
            disabled={working}
            onClick={async () => {
              if (
                (!isDirty && !payNow && !correctionId) ||
                (await confirm('Discard unsaved purchase changes?'))
              )
                navigate(id ? `/purchases/${id}` : '/purchases');
            }}
            className="min-h-11 rounded-xl border border-border px-4 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={working || !workspace.data}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {working && <Loader2 size={16} className="animate-spin" />}
            {id ? 'Save changes' : 'Create purchase'}
          </button>
        </div>
      </form>
    </div>
  );
}
